import { useEffect, useMemo, useState } from 'react'
import { Alert, Badge, Button, FormSelect } from '../ui/primitives'

function installLabel(install) {
  if (install.install_error || install.binding_error) return 'Needs attention'
  if (install.install_status !== 'installed' || install.binding_status !== 'active') return 'Preparing'
  return 'Ready'
}

function healthLabel(status) {
  if (status === 'healthy') return 'Ready'
  if (status === 'unhealthy' || status === 'unavailable') return 'Needs attention'
  return 'Checking'
}

export default function AiRunnerSetupStep({ step, onBack, onRefresh, onApply, onFinish }) {
  const appliances = useMemo(() => step?.appliances || [], [step?.appliances])
  const installs = step?.installs || []
  const [agentId, setAgentId] = useState(appliances[0]?.id || '')
  const [busyAction, setBusyAction] = useState(null)
  const [error, setError] = useState(null)
  const complete = step?.status === 'complete'

  useEffect(() => {
    if (!appliances.some((appliance) => appliance.id === agentId)) {
      setAgentId(appliances[0]?.id || '')
    }
  }, [agentId, appliances])

  const apply = async (action, target) => {
    setBusyAction(`${action}:${target}`)
    setError(null)
    try {
      await onApply(action, target)
    } catch {
      setError('We couldn\'t update AI Runner. Try again.')
    } finally {
      setBusyAction(null)
    }
  }

  const finish = async () => {
    setBusyAction('finish')
    setError(null)
    try {
      await onFinish()
    } catch {
      setError('Setup could not be finished. Refresh the status and try again.')
      await onRefresh()
    } finally {
      setBusyAction(null)
    }
  }

  const selected = installs.find((install) => install.selected)
  const selectedApplianceHasInstall = installs.some((install) => install.agent_id === agentId)

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-3">
        <div>
          <h2 className="text-lg font-medium text-[var(--app-text)]">Install and verify AI Runner v2</h2>
          <p className="mt-1 text-sm text-[var(--app-text-muted)]">
            Choose any initialized appliance. Operations verifies that AI Runner is available before setup can finish.
          </p>
        </div>
        {complete && <Badge color="success">Complete</Badge>}
      </div>

      {(error || step?.error) && <Alert color="danger">{error || 'AI Runner status could not be refreshed. Try again.'}</Alert>}

      <div className="rounded-[var(--app-radius)] border border-[var(--app-border)] p-3 space-y-3">
        <label className="block text-sm" htmlFor="setup-ai-runner-appliance">Install on appliance</label>
        <div className="flex flex-wrap gap-2">
          <FormSelect
            id="setup-ai-runner-appliance"
            value={agentId}
            onChange={(event) => setAgentId(event.target.value)}
            disabled={busyAction !== null}
          >
            {appliances.map((appliance) => <option key={appliance.id} value={appliance.id}>{appliance.name}</option>)}
          </FormSelect>
          {!selectedApplianceHasInstall && (
            <Button color="primary" disabled={!agentId || busyAction !== null} onClick={() => apply('install', agentId)}>
              {busyAction === `install:${agentId}` ? 'Installing…' : 'Install AI Runner v2'}
            </Button>
          )}
        </div>
      </div>

      <div className="space-y-2">
        {installs.length === 0 ? (
          <p className="text-sm text-[var(--app-text-muted)]">AI Runner v2 is not installed yet.</p>
        ) : installs.map((install) => (
          <div key={install.install_id} className="rounded-[var(--app-radius)] border border-[var(--app-border)] bg-[var(--app-surface-2)] p-3 space-y-2">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <div>
                <div className="font-medium text-[var(--app-text)]">{install.agent_name}</div>
              </div>
              <div className="flex items-center gap-2">
                {install.selected && <Badge color="info">Selected</Badge>}
                <Badge color={install.install_status === 'installed' && install.binding_status === 'active' ? 'info' : 'warning'}>{installLabel(install)}</Badge>
              </div>
            </div>
            {(install.install_error || install.binding_error) && <Alert color="warning">AI Runner needs attention. Try the setup again.</Alert>}
            <div className="flex flex-wrap gap-2">
              {!install.selected && <Button disabled={busyAction !== null} onClick={() => apply('select', install.install_id)}>Use this runner</Button>}
              <Button disabled={busyAction !== null} onClick={() => apply('reapply', install.install_id)}>
                {busyAction === `reapply:${install.install_id}` ? 'Trying again…' : 'Try setup again'}
              </Button>
            </div>
          </div>
        ))}
      </div>

      {selected && (
        <div className="rounded-[var(--app-radius)] border border-[var(--app-border)] p-3">
          <div className="flex items-center justify-between gap-2">
            <span className="text-sm font-medium text-[var(--app-text)]">AI Runner availability</span>
            <Badge color={step.health_status === 'healthy' ? 'success' : 'warning'}>
              {healthLabel(step.health_status)}
            </Badge>
          </div>
          {step.health_error && <p className="mt-2 text-xs text-[var(--app-text-muted)]">Refresh the status or try the setup again.</p>}
        </div>
      )}

      <div className="flex items-center justify-between gap-2">
        <Button onClick={onBack}>Back</Button>
        <div className="flex gap-2">
          <Button onClick={onRefresh}>Refresh status</Button>
          {complete && (
            <Button color="primary" disabled={busyAction !== null} onClick={finish}>
              {busyAction === 'finish' ? 'Finishing…' : 'Finish setup'}
            </Button>
          )}
        </div>
      </div>
    </div>
  )
}
